import { useIntl } from "react-intl";
import {Modal, PopupProvider} from "../../UIElements";
import React, { useState, useRef } from "react";
import {useSupportBotContext} from "./SupportBotData";
import {useVersionDifferences, VersionDifferenceIndicator} from "./VersionDifferences";
import "./BotMaterials.scss";
import {File, Trash, Plus, AlertTriangle} from "lucide-react";

const BotMaterials = ({ botData, updateBotData }) => {
    const intl = useIntl();
    const { materials } = botData;
    const fileInputRef = useRef(null);

    // Version difference helpers
    const {
        isBotFieldDifferent,
        getMissingMaterials,
        findDefaultBot
    } = useVersionDifferences();

    // Check if materials array differs from default
    const areMaterialsDifferent = isBotFieldDifferent(
        { config: botData },
        "config.materials"
    );

    // Get materials that are in default bot but not in this version
    const missingMaterials = getMissingMaterials({ config: botData });

    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        index: null
    });

    const openDeleteConfirmation = (index) => {
        setDeleteConfirmation({
            isOpen: true,
            index
        });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({
            isOpen: false,
            index: null
        });
    };

    const removeMaterial = () => {
        const { index } = deleteConfirmation;
        const updatedMaterials = materials.filter((_, i) => i !== index);
        updateBotData("materials", updatedMaterials);
        closeDeleteConfirmation();
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Just add the filename to the materials array
            updateBotData("materials", [
                ...materials,
                file.name
            ]);
        }
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Simplified to just trigger the file input
    const handleAddMaterial = () => {
        fileInputRef.current.click();
    };

    // Add a missing material back to this version
    const handleAddMissingMaterial = (material) => {
        if (material && !materials.includes(material)) {
            updateBotData("materials", [
                ...materials,
                material
            ]);
        }
    };

    // Get difference message for a material
    const getMaterialDifferenceMessage = (material, isNewMaterial) => {
        if (isNewMaterial) {
            return intl.formatMessage({
                id: 'bot.materials.difference.new',
                defaultMessage: 'Material added in this version'
            });
        }

        return intl.formatMessage({
            id: 'bot.materials.difference.modified',
            defaultMessage: 'Different from default version'
        });
    };

    return (
        <PopupProvider>
            <h4 className="field-header">
                {intl.formatMessage({
                    id: 'referenceMaterials',
                    defaultMessage: 'Materials',
                })}
            </h4>
            <div className={"materials-list"}>
                {materials.map((material, index) => {
                    // Get the default bot to check if this material exists there
                    const defaultBot = findDefaultBot({ config: botData });
                    const defaultMaterials = defaultBot?.config?.materials || [];

                    // Determine if this material is unique to this version (not in default)
                    const isMaterialUnique = defaultBot && !defaultMaterials.includes(material);

                    const differenceTitle = isMaterialUnique
                        ? intl.formatMessage({
                            id: 'bot.materials.difference.unique',
                            defaultMessage: 'Material added in this version (not in default)'
                        })
                        : "";

                    return (
                        <div key={index} className={`material-item ${isMaterialUnique ? 'version-different' : ''}`}>
                            <File />
                            <span className="material-name">
                                {material}
                                {isMaterialUnique && (
                                    <VersionDifferenceIndicator differenceTitle={differenceTitle} />
                                )}
                            </span>
                            <button
                                className={"remove-material"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirmation(index);
                                }}
                                title={intl.formatMessage({
                                    id: 'bot.materials.remove',
                                    defaultMessage: 'Remove',
                                })}
                            >
                                <Trash />
                            </button>
                        </div>
                    );
                })}

                {/* Missing materials section */}
                {missingMaterials.length > 0 && (
                    <>
                        {missingMaterials.map((material, index) => {
                            const differenceTitle = intl.formatMessage({
                                id: 'bot.materials.notIncluded',
                                defaultMessage: 'Material not included in this version'
                            });

                            return (
                                <div key={`missing-${index}`} className="material-item missing-material">
                                    <File />
                                    <span className="material-name">
                                        {material}
                                    </span>
                                    <VersionDifferenceIndicator differenceTitle={differenceTitle} />
                                    <button
                                        className={"remove-material add-material"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddMissingMaterial(material);
                                        }}
                                        title={intl.formatMessage({
                                            id: 'bot.materials.add',
                                            defaultMessage: 'Add to this version',
                                        })}
                                    >
                                        <Plus size={22} />
                                    </button>
                                </div>
                            );
                        })}
                    </>
                )}
                <p className={"placeholder"}>
                    <AlertTriangle />
                    {intl.formatMessage({
                        id:'materialsFeatureMissingPlaceholder',
                        defaultMessage:'Adding materials to modules is still in development. Adding materials here will NOT add the material to the module. Contact the Annie team if you wish to use external materials.'
                    })}
                </p>

                <button className={"new-topic"} onClick={handleAddMaterial}>
                    + {intl.formatMessage({
                    id: 'bot.materials.add',
                    defaultMessage: 'Add Material',
                })}
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.isOpen && (
                <Modal
                    header={intl.formatMessage({
                        id: 'delete.material',
                        defaultMessage: 'Delete material',
                    })}
                    text={intl.formatMessage({
                        id: 'delete.material.text',
                        defaultMessage: 'Are you sure you want to delete this material?',
                    })}
                    closeModal={closeDeleteConfirmation}
                    confirmAction={removeMaterial}
                    confirmText={intl.formatMessage({
                        id: 'survey.delete',
                        defaultMessage: 'Delete',
                    })}
                    alert={true}
                />
            )}
        </PopupProvider>
    );
};

export default BotMaterials;